import { useEffect, useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Grid,
    Button,
    TextField,
    Select,
    MenuItem,
    Paper,
    InputLabel,
    FormControl,
    Skeleton,
    Chip
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoneyIcon from '@mui/icons-material/Money';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import apiClient from '../../../services/api-client';

const statutConfig = {
    'EMISE': { label: 'Émise', color: '#1565c0', bgColor: 'rgba(21, 101, 192, 0.08)', icon: <AccessTimeIcon sx={{ fontSize: 14 }} /> },
    'PAYEE': { label: 'Payée', color: '#2e7d32', bgColor: 'rgba(46, 125, 50, 0.08)', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    'ANNULEE': { label: 'Annulée', color: '#757575', bgColor: 'rgba(117, 117, 117, 0.08)', icon: <CancelIcon sx={{ fontSize: 14 }} /> },
    'EN_RETARD': { label: 'En retard', color: '#c62828', bgColor: 'rgba(198, 40, 40, 0.08)', icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    'PARTIELLEMENT_PAYEE': { label: 'Partielle', color: '#ef6c00', bgColor: 'rgba(239, 108, 0, 0.08)', icon: <AttachMoneyIcon sx={{ fontSize: 14 }} /> },
};

const methodConfig = {
    'ESPECES': { label: 'Espèces', icon: <MoneyIcon sx={{ fontSize: 18 }} />, color: '#2e7d32' },
    'CARTE_BANCAIRE': { label: 'Carte bancaire', icon: <CreditCardIcon sx={{ fontSize: 18 }} />, color: '#1565c0' },
    'VIREMENT': { label: 'Virement', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />, color: '#6a1b9a' },
    'CHEQUE': { label: 'Chèque', icon: <ApartmentIcon sx={{ fontSize: 18 }} />, color: '#ef6c00' },
    'MOBILE_MONEY': { label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 18 }} />, color: '#388e3c' },
};


const PaymentDetailSheet = ({ invoice, open, onClose, onAddPayment }) => {

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);



    useEffect(() => {
        const fetchPayments = async () => {
            try {
                const res = await apiClient.get(`/api/invoices/${invoice.id}/details`);
                setDetails(res.data);
            } catch (error) {
                console.error('Error fetching payments:', error);
                setDetails(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, [invoice]);


    // On s'assure d'avoir des valeurs par défaut (0) pendant que details est null
    const totalPaid = details?.paiements?.reduce((sum, p) => sum + p.montant, 0) || 0;
    const total = details?.montantTotal || 0;
    const montantPaye = details?.montantPaye || 0;

    const remaining = Math.max(0, total - montantPaye);
    const progressPercent = total > 0 ? Math.min(100, (montantPaye / total) * 100) : 0;

    // La correction est ici : on rajoute ( ... || [] )
    const sortedPayments = [...(details?.paiements || [])].sort(
        (a, b) => new Date(b.datePaiement).getTime() - new Date(a.datePaiement).getTime()
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: '50vw' },
                    backgroundColor: '#faf7f2',
                    p: 3,
                },
            }}
        >
            <Box sx={{ mt: 2, width: '50vw' }}>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontFamily: "'Cormorant Garamond', serif", color: '#1a1a2e' }}>
                        Détail Paiement — {details?.numeroFacture}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Invoice Summary */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid rgba(201, 168, 76, 0.12)',
                        p: 2,
                        mb: 2.5,
                    }}
                >
                    {[
                        { label: 'Client', value: details?.clientInfo?.nomComplet, valueStyle: {} },
                        { label: 'Total TTC', value: `${total.toLocaleString('fr-FR')}`, valueStyle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700 } },
                    ].map(({ label, value, valueStyle }) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>{label}</Typography>
                            <Typography variant="body2" sx={{ color: '#1a1a2e', fontWeight: 500, ...valueStyle }}>{value}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{ my: 1.5, borderColor: 'rgba(201, 168, 76, 0.1)' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>Total Payé</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>{details?.montantPaye.toLocaleString('fr-FR')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>Reste à Payer</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: remaining > 0 ? '#c62828' : '#2e7d32' }}>
                            {remaining.toLocaleString('fr-FR')}
                        </Typography>
                    </Box>

                    <Box sx={{ height: 12, borderRadius: 6, overflow: 'hidden', backgroundColor: 'rgba(201, 168, 76, 0.1)', mb: 1 }}>
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            style={{
                                height: '100%',
                                borderRadius: 2,
                                background:
                                    progressPercent >= 100
                                        ? 'linear-gradient(135deg, #1b5e20, #43a047)'
                                        : 'linear-gradient(135deg, #b08d3a, #c9a84c)',
                            }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#888', fontSize: 11 }}>
                        {progressPercent >= 100
                            ? 'Facture soldée ✓'
                            : `${progressPercent.toFixed(0)}% payé — ${details?.paiements?.length} versement${details?.paiements?.length > 1 ? 's' : ''}`}
                    </Typography>
                </Paper>

                {/* Add Payment Button */}
                {details?.statut !== 'ANNULEE' && details?.statut !== 'PAYEE' && remaining > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            startIcon={<AddIcon />}
                            sx={{
                                mb: 3,
                                height: 44,
                                background: 'linear-gradient(135deg, #b08d3a, #c9a84c)',
                                color: 'white',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontFamily: "'DM Sans', sans-serif",
                                '&:hover': { background: 'linear-gradient(135deg, #9a7a30, #b8983e)' },
                            }}
                            onClick={() => { onClose(); onAddPayment(invoice); }}
                        >
                            Enregistrer un versement
                        </Button>
                    </Box>
                )}

                {/* Payment History */}
                <Typography variant="subtitle1" sx={{ fontFamily: "'Cormorant Garamond', serif", color: '#1a1a2e', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, color: '#c9a84c' }} />
                    Échéancier de paiement
                </Typography>

                {sortedPayments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5, border: '1px solid rgba(201, 168, 76, 0.08)', borderRadius: 2 }}>
                        <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#ccc', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: '#aaa' }}>Aucun paiement enregistré</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {sortedPayments.map((payment, idx) => {
                            const method = methodConfig[payment.modePaiement];
                            const isFirst = idx === 0;
                            const isLast = idx === sortedPayments.length - 1;
                            return (
                                <motion.div
                                    key={payment.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            border: '1px solid rgba(201, 168, 76, 0.08)',
                                            borderRadius: 2,
                                            p: 1.5,
                                            backgroundColor: 'white',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 1.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: `${method.color}15`,
                                                        color: method.color,
                                                    }}
                                                >
                                                    {method.icon}
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a2e', display: 'block' }}>
                                                        Versement #{sortedPayments?.length - idx}
                                                        {isFirst && (
                                                            <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: '#c9a84c' }}>— Dernier</Box>
                                                        )}
                                                        {isLast && sortedPayments?.length > 1 && (
                                                            <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: '#2e7d32' }}>— Premier</Box>
                                                        )}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#999', fontSize: 10 }}>
                                                        {new Date(payment.datePaiement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                        {payment.codePaiement && ` • Ref: ${payment.codePaiement}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', fontFamily: "'Cormorant Garamond', serif" }}>
                                                {payment.montant.toLocaleString('fr-FR')}
                                            </Typography>
                                        </Box>
                                        {payment.commentaire && (
                                            <Typography variant="caption" sx={{ display: 'block', pl: 6, color: '#888', mt: 0.5 }}>
                                                {payment.commentaire}
                                            </Typography>
                                        )}
                                    </Paper>
                                </motion.div>
                            );
                        })}
                    </Box>
                )}

            </Box>
        </Drawer>
    );
};

export default PaymentDetailSheet;